import { motion } from "framer-motion";
import { Cookie, Coffee, CupSoda, Download, Flame } from "lucide-react";
import { KIOSK_MENU } from "@/lib/site";

const CATS = {
  "Hot Food": { icon: Flame, color: "#e0452c" },
  Snacks: { icon: Cookie, color: "#7ca836" },
  "Hot Drinks": { icon: Coffee, color: "#f2a413" },
  "Cold Drinks": { icon: CupSoda, color: "#4fb8cf" },
};

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

export default function KioskMenu() {
  return (
    <section id="kiosk-menu" className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="kiosk-menu-section">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.7 }}
        className="flex flex-wrap items-center justify-between gap-8"
      >
        <div className="max-w-xl">
          <h2 className="text-4xl leading-tight text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)] sm:text-5xl" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>KIOSK &amp; BAR MENU</h2>
          <a
            href="/kiosk-menu-2026.pdf"
            download="Cool-Runnings-Kiosk-Menu-2026.pdf"
            className="btn-gold mt-6 !px-6 !py-3 !text-xs"
            data-testid="kiosk-menu-download-btn"
          >
            <Download size={15} /> Download the menu
          </a>
        </div>
        <div className="relative" data-testid="kiosk-menu-cartoon-wrap">
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, scale: 0.4, x: -14 }}
              whileInView={{ opacity: [0, 0.75, 0], scale: [0.4, 1.3, 1.9], x: -26 - i * 20 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 1.7, delay: 0.25 + i * 0.35, ease: "easeOut" }}
              className="pointer-events-none absolute top-1/2 h-16 w-16 -translate-y-1/2 rounded-full bg-white/85 blur-md"
              aria-hidden="true"
            />
          ))}
          <motion.img
            initial={{ opacity: 0, rotate: 6, x: -90 }}
            whileInView={{ opacity: 1, rotate: 3, x: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ type: "spring", stiffness: 60, damping: 14, delay: 0.15 }}
            src="/kiosk-cartoon.png"
            alt="Cartoon kids riding the toboggan with a hot dog and fries — sliding, yum!"
            className="relative w-44 rotate-3 drop-shadow-[0_14px_24px_rgba(36,28,13,0.28)] sm:w-60"
            data-testid="kiosk-menu-cartoon"
          />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="mt-12 overflow-hidden rounded-3xl border-2 border-[#241c0d]/12 bg-[#faf5ea] shadow-[0_20px_60px_rgba(36,28,13,0.35)]"
        data-testid="kiosk-menu-board"
      >
        <div className="h-2.5 w-full bg-[linear-gradient(90deg,#4fb8cf,#f2a413,#8cc440,#e0452c,#e77fb3)]" />
        <div className="grid gap-10 p-6 sm:p-10 lg:grid-cols-2">
          {KIOSK_MENU.map((cat) => {
            const { icon: Icon, color } = CATS[cat.category] || { icon: Flame, color: "#f2a413" };
            return (
              <div key={cat.category} data-testid={`kiosk-category-${slug(cat.category)}`}>
                <div
                  className="flex items-center gap-3 rounded-2xl px-4 py-3 text-white shadow-[0_6px_18px_rgba(36,28,13,0.18)]"
                  style={{ backgroundColor: color }}
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/25">
                    <Icon size={17} />
                  </span>
                  <h3 className="text-2xl" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>{cat.category}</h3>
                </div>
                <ul className="mt-4 space-y-3.5">
                  {cat.items.map((item) => (
                    <li key={item.name} data-testid="kiosk-item">
                      <div className="flex items-baseline gap-2">
                        <span className="font-extrabold leading-snug text-[#241c0d]">{item.name}</span>
                        <span className="min-w-6 flex-1 border-b border-dotted" style={{ borderColor: `${color}59` }} />
                        <span className="font-display shrink-0 text-lg" style={{ color }}>
                          {item.price}
                        </span>
                      </div>
                      {item.detail && <p className="mt-0.5 text-sm font-medium text-[#8a7b5e]">{item.detail}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <p className="mx-6 border-t-2 border-dotted border-[#241c0d]/25 px-2 pb-8 pt-5 text-center text-xs font-bold uppercase tracking-widest text-[#8a7b5e] sm:mx-10">
          All prices include VAT · Order at the kiosk or bar on the day
        </p>
      </motion.div>
    </section>
  );
}
